import careConfig from "@careConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { navigate, usePath } from "raviger";
import { useCallback, useEffect, useState } from "react";

import Loading from "@/components/Common/Loading";
import { notifyThemeScopeChange } from "@/Providers/ThemeProvider";

import { AuthUserContext } from "@/hooks/useAuthUser";

import { LocalStorageKeys } from "@/common/constants";

import { userAtom } from "@/atoms/user-atom";
import {
  JwtTokenObtainPair,
  LoginResponse,
  MfaAuthenticationToken,
} from "@/types/auth/auth";
import authApi from "@/types/auth/authApi";
import { TokenData } from "@/types/otp/otp";
import userApi from "@/types/user/userApi";
import mutate from "@/Utils/request/mutate";
import query from "@/Utils/request/query";
import { HTTPError } from "@/Utils/request/types";

interface Props {
  children: React.ReactNode;
  unauthorized: React.ReactNode;
  otpAuthorized: React.ReactNode;
}

const isMFAResponse = (data: LoginResponse): data is MfaAuthenticationToken => {
  return "temp_token" in data;
};

const isJwtTokenResponse = (
  data: LoginResponse,
): data is JwtTokenObtainPair => {
  return "access" in data && "refresh" in data;
};

const isTransientRefreshError = (error: unknown) => {
  if (!(error instanceof HTTPError)) {
    return true;
  }

  return error.status >= 500;
};

const isAuthRefreshError = (error: unknown) => {
  return error instanceof HTTPError && [400, 401, 403].includes(error.status);
};

export default function AuthUserProvider({
  children,
  unauthorized,
  otpAuthorized,
}: Props) {
  const queryClient = useQueryClient();
  const initialAccessToken = localStorage.getItem(LocalStorageKeys.accessToken);
  const initialRefreshToken = localStorage.getItem(
    LocalStorageKeys.refreshToken,
  );
  const [accessToken, setAccessToken] = useState(initialAccessToken);
  const [refreshToken, setRefreshToken] = useState(initialRefreshToken);
  const [authReady, setAuthReady] = useState(
    !initialRefreshToken || !!initialAccessToken,
  );
  const path = usePath();
  const [patientToken, setPatientToken] = useState<TokenData | null>(
    JSON.parse(
      localStorage.getItem(LocalStorageKeys.patientTokenKey) || "null",
    ),
  );

  const resetAuthState = useCallback(async () => {
    await queryClient.cancelQueries({ queryKey: ["initial-auth-refresh"] });
    await queryClient.cancelQueries({ queryKey: ["user-refresh-token"] });
    localStorage.removeItem(LocalStorageKeys.accessToken);
    localStorage.removeItem(LocalStorageKeys.refreshToken);
    localStorage.removeItem(LocalStorageKeys.patientTokenKey);
    setAccessToken(null);
    setRefreshToken(null);
    setPatientToken(null);
    setAuthReady(true);
    notifyThemeScopeChange();

    await queryClient.resetQueries({ queryKey: ["currentUser"] });

    const redirectURL = getRedirectURL();
    navigate(redirectURL ? `/login?redirect=${redirectURL}` : "/login");
  }, [queryClient]);

  const initialTokenRefreshQuery = useQuery({
    queryKey: ["initial-auth-refresh"],
    queryFn: ({ signal }) => {
      return query(authApi.tokenRefresh, {
        body: { refresh: refreshToken || "" },
      })({ signal });
    },
    retry: (failureCount, error) =>
      isTransientRefreshError(error) && failureCount < 2,
    enabled: !!refreshToken && !accessToken,
  });

  useEffect(() => {
    if (initialTokenRefreshQuery.isError) {
      if (accessToken) {
        setAuthReady(true);
        return;
      }

      if (isAuthRefreshError(initialTokenRefreshQuery.error)) {
        void resetAuthState();
      } else {
        setAuthReady(true);
      }
      return;
    }

    if (initialTokenRefreshQuery.data) {
      const { access, refresh } = initialTokenRefreshQuery.data;
      setAccessToken(access);
      setRefreshToken(refresh);
      localStorage.setItem(LocalStorageKeys.accessToken, access);
      localStorage.setItem(LocalStorageKeys.refreshToken, refresh);
      setAuthReady(true);
    }
  }, [
    accessToken,
    initialTokenRefreshQuery.data,
    initialTokenRefreshQuery.error,
    initialTokenRefreshQuery.isError,
    resetAuthState,
  ]);

  const { data: user, isLoading } = useQuery({
    queryKey: ["currentUser"],
    queryFn: query(userApi.currentUser, { silent: true }),
    retry: false,
    enabled: !!accessToken && authReady,
  });

  const setUser = useSetAtom(userAtom);
  useEffect(() => {
    setUser(user);
  }, [user, setUser]);

  const tokenRefreshQuery = useQuery({
    queryKey: ["user-refresh-token"],
    queryFn: ({ signal }) => {
      return query(authApi.tokenRefresh, {
        body: { refresh: refreshToken || "" },
      })({ signal });
    },
    refetchIntervalInBackground: true,
    refetchInterval: careConfig.auth.tokenRefreshInterval,
    enabled: !!refreshToken && !!user,
  });

  useEffect(() => {
    if (tokenRefreshQuery.isError) {
      if (isAuthRefreshError(tokenRefreshQuery.error)) {
        void resetAuthState();
      }
      return;
    }

    if (tokenRefreshQuery.data) {
      const { access, refresh } = tokenRefreshQuery.data;
      setAccessToken(access);
      setRefreshToken(refresh);
      localStorage.setItem(LocalStorageKeys.accessToken, access);
      localStorage.setItem(LocalStorageKeys.refreshToken, refresh);
    }
  }, [
    tokenRefreshQuery.data,
    tokenRefreshQuery.error,
    tokenRefreshQuery.isError,
    resetAuthState,
  ]);

  const { mutateAsync: signIn, isPending: isAuthenticating } = useMutation({
    mutationFn: mutate(authApi.login),
    onSuccess: async (data: LoginResponse) => {
      if (isMFAResponse(data)) {
        localStorage.setItem("mfa_temp_token", data.temp_token);
        const redirectURL = getRedirectURL();
        const directURL = path !== "/login" ? window.location.href : null;
        navigate(
          redirectURL
            ? `/2fa?redirect=${redirectURL}`
            : directURL
              ? `/2fa?redirect=${directURL}`
              : "/2fa",
        );
        return;
      }

      if (isJwtTokenResponse(data)) {
        await queryClient.cancelQueries({ queryKey: ["initial-auth-refresh"] });
        await queryClient.cancelQueries({ queryKey: ["user-refresh-token"] });
        setAccessToken(data.access);
        setRefreshToken(data.refresh);
        setAuthReady(true);
        localStorage.setItem(LocalStorageKeys.accessToken, data.access);
        localStorage.setItem(LocalStorageKeys.refreshToken, data.refresh);
        notifyThemeScopeChange();

        await queryClient.invalidateQueries({ queryKey: ["currentUser"] });
        if (path === "/" || path === "/login") {
          navigate(getRedirectOr("/"));
        }
      }
    },
  });

  const { mutateAsync: verifyMFA, isPending: isVerifyingMFA } = useMutation({
    mutationFn: mutate(authApi.mfa.login),
    onSuccess: async (data: JwtTokenObtainPair) => {
      localStorage.removeItem("mfa_temp_token");

      await queryClient.cancelQueries({ queryKey: ["initial-auth-refresh"] });
      await queryClient.cancelQueries({ queryKey: ["user-refresh-token"] });
      setAccessToken(data.access);
      setRefreshToken(data.refresh);
      setAuthReady(true);
      localStorage.setItem(LocalStorageKeys.accessToken, data.access);
      localStorage.setItem(LocalStorageKeys.refreshToken, data.refresh);
      notifyThemeScopeChange();

      await queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      navigate(getRedirectOr("/"));
    },
  });

  const patientLogin = (tokenData: TokenData, redirectUrl: string) => {
    setPatientToken(tokenData);
    localStorage.setItem(
      LocalStorageKeys.patientTokenKey,
      JSON.stringify(tokenData),
    );
    notifyThemeScopeChange();
    navigate(redirectUrl);
  };

  const signOut = useCallback(async () => {
    const currentAccessToken =
      accessToken ?? localStorage.getItem(LocalStorageKeys.accessToken);
    const currentRefreshToken =
      refreshToken ?? localStorage.getItem(LocalStorageKeys.refreshToken);

    if (currentAccessToken && currentRefreshToken) {
      try {
        await mutate(authApi.logout)({
          access: currentAccessToken,
          refresh: currentRefreshToken,
        });
      } catch (error) {
        console.error("Error during logout:", error);
      }
    }

    await resetAuthState();
  }, [accessToken, refreshToken, resetAuthState]);

  // Handles signout from current tab, if signed out from another tab.
  useEffect(() => {
    const listener = (event: StorageEvent) => {
      if (!event.key) {
        return;
      }

      if (
        !event.newValue &&
        [
          LocalStorageKeys.accessToken,
          LocalStorageKeys.refreshToken,
          LocalStorageKeys.patientTokenKey,
        ].includes(event.key)
      ) {
        void resetAuthState();
        return;
      }

      if (event.key === LocalStorageKeys.accessToken) {
        setAccessToken(event.newValue);
        setAuthReady(Boolean(event.newValue));
        void queryClient.cancelQueries({ queryKey: ["initial-auth-refresh"] });
        void queryClient.cancelQueries({ queryKey: ["user-refresh-token"] });
        return;
      }

      if (event.key === LocalStorageKeys.refreshToken) {
        setRefreshToken(event.newValue);
        void queryClient.cancelQueries({ queryKey: ["initial-auth-refresh"] });
        void queryClient.cancelQueries({ queryKey: ["user-refresh-token"] });
        return;
      }

      if (event.key === LocalStorageKeys.patientTokenKey) {
        try {
          setPatientToken(
            event.newValue ? (JSON.parse(event.newValue) as TokenData) : null,
          );
        } catch {
          setPatientToken(null);
        }
      }
    };

    addEventListener("storage", listener);

    return () => {
      removeEventListener("storage", listener);
    };
  }, [queryClient, resetAuthState]);

  if (isLoading || (!!refreshToken && !authReady)) {
    return <Loading />;
  }

  return (
    <AuthUserContext.Provider
      value={{
        signIn,
        signOut,
        verifyMFA,
        isAuthenticating,
        isVerifyingMFA,
        user,
        patientLogin,
        patientToken,
      }}
    >
      {user ? children : patientToken?.token ? otpAuthorized : unauthorized}
    </AuthUserContext.Provider>
  );
}

const getRedirectURL = () => {
  return new URLSearchParams(window.location.search).get("redirect");
};

const getRedirectOr = (fallback: string) => {
  const url = getRedirectURL();

  if (url) {
    try {
      const redirect = new URL(url);
      if (window.location.origin === redirect.origin) {
        return redirect.pathname + redirect.search;
      }
      console.error("Redirect does not belong to same origin.");
    } catch {
      console.error(`Invalid redirect URL: ${url}`);
    }
  }

  return fallback;
};
